import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
  AccountManagementAuthenticationModel,
  UserToAddModel,
  ZenityUserRoleEnum,
} from 'src/app/models/api/accountManagement.model';
import { ISelectOption } from 'src/app/models/selectOptions';
import { RoleSelectOptions } from 'src/app/models/selectOptions/roleSelectOptions.model';
import { AccountManagementHttpService } from 'src/app/services/httpServices/accountManagementHttpService/account-management-http-service.service';
import { NotificationsService } from 'src/app/services/infra/notifications/notifications.service';

export interface IInviteUserDialogLegacyInput {
  authentications: AccountManagementAuthenticationModel[];
}

@Component({
  selector: 'app-invite-user-dialog-legacy',
  templateUrl: './invite-user-dialog-legacy.component.html',
  styleUrls: ['./invite-user-dialog-legacy.component.scss'],
})
export class InviteUserDialogLegacyComponent implements OnInit {
  public inviteUserFormGroup: UntypedFormGroup;

  public roles: ISelectOption[] = RoleSelectOptions;
  public authentications: ISelectOption[];

  public waitingForResponse = false;

  private _roleSelected: ISelectOption | undefined;

  constructor(
    public dialogRef: MatDialogRef<InviteUserDialogLegacyComponent>,
    @Inject(MAT_DIALOG_DATA) public details: IInviteUserDialogLegacyInput,
    private readonly _formBuilder: UntypedFormBuilder,
    private readonly _accountManagementHttpService: AccountManagementHttpService,
    private readonly _notificationService: NotificationsService
  ) {}

  ngOnInit(): void {
    this._buildSelectOptionsForAuthentications();
    let auth_id = '';

    if (this.authentications.length == 1) {
      auth_id = this.authentications[0].id;
    }

    this.inviteUserFormGroup = this._formBuilder.group({
      userEmail: this._formBuilder.control('', [Validators.required, Validators.email]),
      role: this._formBuilder.control(this._roleSelected?.displayName, [Validators.required]),
      authentication_id: this._formBuilder.control(auth_id, [Validators.required]),
    });
  }

  public onClose(): void {
    this.dialogRef.close(false);
  }

  public onRoleSelection(selectedRole: ISelectOption) {
    this._roleSelected = this.roles.find((type) => type.id === selectedRole.id);
    this.inviteUserFormGroup.controls['role'].setValue(this._roleSelected?.displayName);
  }

  public onAuthenticationSelection(selectedAuthentication: ISelectOption) {
    this.inviteUserFormGroup.controls['authentication_id'].setValue(selectedAuthentication.id);
  }

  public inviteUser() {
    this.waitingForResponse = true;
    const role: ZenityUserRoleEnum =
      ZenityUserRoleEnum[this.inviteUserFormGroup.controls['role'].value.toLowerCase() as ZenityUserRoleEnum];
    this._accountManagementHttpService
      .inviteUser({
        email: this.inviteUserFormGroup.controls['userEmail'].value,
        role: role,
        authentication_id: this.inviteUserFormGroup.controls['authentication_id'].value,
      } as UserToAddModel)
      .subscribe(
        () => {
          this._notificationService.success('Invitation sent successfully');
          this.dialogRef.close(true);
        },
        (error) => {
          if (error.status == 422) {
            const detail: unknown = error?.error?.detail;
            if (typeof detail === 'string' && detail.toLowerCase().includes('is not allowed in the account')) {
              this._notificationService.warning(
                'The email ' +
                  this.inviteUserFormGroup.controls['userEmail'].value +
                  'domain of this invitee is not allowed. Please use an approved domain.'
              );
            } else {
              this._notificationService.warning(
                'User ' + this.inviteUserFormGroup.controls['userEmail'].value + ' already invited or a member'
              );
            }
          }
          this.waitingForResponse = true;
          this.dialogRef.close(false);
        }
      );
  }

  private _buildSelectOptionsForAuthentications() {
    const authenticationsSelectOptions: ISelectOption[] = [];
    this.details.authentications.forEach((authentication) => {
      authenticationsSelectOptions.push({
        id: authentication.id,
        value: authentication.id,
        displayName: authentication.name,
      });
    });
    this.authentications = authenticationsSelectOptions;
  }
}
